import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import GanttChart from './GanttChart';
import getEmployeeProjectFilters from './EmployeeProjectFilters';

import { getIdMap } from '../../helpers/helpers';
import { msToHours } from '../../helpers/time';
import {
  filterOnCols,
  failsProjectFilter,
  failsFilter,
  failsCostcodeFilter,
  getInitialFilters,
  getCostcodeInitialOptions,
  getReferencedCostcodeIds,
} from '../reportHelpers';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';
import {
  searchCostcodeOptions,
  getCostcodeByIds,
} from '../../costcodes/state/costcodes.actions';

const compareIfValid = (reference, d2, compare) => {
  if (d2 && d2.isValid()) return compare(reference, d2);
  return reference;
};

const isValid = (d) => d && moment.isMoment(d) && d.isValid();

const compareMoments = (d1, d2, compare) => {
  if (!(isValid(d1))) return d2;
  if (!(isValid(d2))) return d1;
  return compare(d1, d2);
};

class ProjectTimeline extends React.Component {
  constructor(props) {
    super(props);
    const {
      height = 450,
      costcodes = [],
      projects = [],
      phases = [],
      allProjects = [],
      allPhases = [],
      selectAllProjects = false,
      selectAllPhases = false,
      selectAllCostcodes = false,
      hasNullProject,
      hasNullPhase,
      hasNullCostcode,
      columns,
      settings,
    } = this.props;

    this.checkedColumns = new Set(['phase', 'costcode', 'estimatedHours', 'actualHours'].filter(filterOnCols(columns)));
    this.state = {
      height,
      enabledFilters: {
        project: true,
        costcode: true,
        estimatedHours: true,
        actualHours: true,
      },
      ...getInitialFilters({
        addUsers: false,
        selectAllProjects,
        allProjects,
        projects,
        selectAllCostcodes,
        costcodes,
        phases,
        selectAllPhases,
        allPhases,
        hasNullProject,
        hasNullPhase,
        hasNullCostcode,
        settings,
      }),
    };

    this.onResize = this.resize.bind(this);
    this.onFilterToggle = this.toggleFilter.bind(this);

    this.projectIdMap = getIdMap(allProjects);
    this.phaseMap = getIdMap(allPhases);
  }

  componentDidMount() {
    const {
      onRef,
    } = this.props;

    onRef(this);
    this.resize();
    this.fetchReferencedCostcodes();
  }

  componentDidUpdate(prevProps) {
    const {
      height: prevHeight,
      timeEntryUserMap: prevTimeEntryUserMap,
    } = prevProps;
    const {
      height,
      timeEntryUserMap,
    } = this.props;
    if (height !== prevHeight) {
      this.setState({
        height,
      });
    }
    if (timeEntryUserMap !== prevTimeEntryUserMap) {
      this.fetchReferencedCostcodes();
    }
  }

  componentWillUnmount() {
    const {
      onRef,
    } = this.props;

    onRef(null);
  }

  getRelevantPhases() {
    const {
      allPhases = [],
      settings = {},
    } = this.props;
    const {
      projects: selectedProjects,
    } = this.state;
    return selectedProjects.size === 0
      ? allPhases : allPhases.filter((phase) => {
        const project = this.projectIdMap[phase.projectId];
        return project && selectedProjects.has(
          formatProjectLabelFromCompanySettings({
            name: project.name,
            number: project.number,
            settings,
          }),
        );
      });
  }

  getColFilters() {
    const {
      allProjects = [],
      allPhases = [],
      t,
      settings = {},
      searchCostcodeOptions: searchCostcodeOptionsAction,
    } = this.props;

    const {
      projects,
      phases,
      costcodes,
    } = this.state;

    const filters = getEmployeeProjectFilters({
      onFilterToggle: (type, checked) => {
        this.setState({
          [type]: checked,
        });
      },
      onCheckChanged: (type, checked) => this.updateRowFilters(type, checked),
      projects: allProjects,
      showNoneCostcodeOption: false,
      showGlobalCostcodes: false,
      costcodeFetchOptions: searchCostcodeOptionsAction,
      costcodeInitialOptions: getCostcodeInitialOptions(
        this.props.costcodes,
        { includeNone: false },
      ),
      costcodeProjects: allProjects,
      costcodePhases: allPhases,
      phases: this.getRelevantPhases(),
      showEmployee: false,
      projectCheckDisabled: true,
      checkedColumns: this.checkedColumns,
      t,
      settings,
      filters: {
        projects,
        phases,
        costcodes,
      },
    });
    return filters.concat([
      {
        title: 'Estimated Hours',
        key: 'estimatedHours',
        onCheckChanged: (checked) => this.updateRowFilters('estimatedHours', checked),
        checked: this.checkedColumns.has('estimatedHours'),
      }, {
        title: 'Actual Hours',
        key: 'actualHours',
        onCheckChanged: (checked) => this.updateRowFilters('actualHours', checked),
        checked: this.checkedColumns.has('actualHours'),
      },
    ]);
  }

  fetchReferencedCostcodes() {
    const { timeEntryUserMap = {}, getCostcodeByIds: fetchByIds } = this.props;
    const ids = getReferencedCostcodeIds(timeEntryUserMap);
    if (ids.length) fetchByIds(ids);
  }

  toggleFilter(item, checked) {
    const {
      key: itemKey,
    } = item;

    if (checked) {
      this.checkedColumns.add(itemKey);
    } else {
      this.checkedColumns.delete(itemKey);
    }
  }

  resize() {
    const {
      height,
    } = this.props;
    this.setState({
      height,
    });
  }

  updateRowFilters(key, checked) {
    this.setState({
      enabledFilters: {
        ...this.state.enabledFilters,
        [key]: checked,
      },
    });
    this.toggleFilter({ key }, checked);
  }

  formatData() {
    const {
      users = [],
      projects = [],
      timeEntryUserMap = {},
      settings = {},
      costcodeIdMap = {},
    } = this.props;
    const {
      enabledFilters,
      projects: selectedProjects,
      phases: selectedPhases,
      costcodes,
    } = this.state;

    const projectTimeMaps = {};
    const projectActualHoursMap = {};

    const relevantProj = [];

    const {
      estimatedHours,
      actualHours,
    } = enabledFilters;
    if (!estimatedHours && !actualHours) return { projects: [] };

    if (actualHours) {
      users.forEach((user) => {
        const tasks = timeEntryUserMap[user?.id] ?? [];
        tasks.forEach((task) => {
          if (!task.startTime || !task.endTime) return;
          if (!task.projectId) return;
          const project = this.projectIdMap[task.projectId];
          if (failsProjectFilter(selectedProjects, project, settings)) return;

          const phase = this.phaseMap[task.costcodeId];
          if (failsFilter(selectedPhases, phase ? { name: phase?.name } : null)) {
            return;
          }

          const costcode = costcodeIdMap[task.costcodeId];
          if (failsCostcodeFilter(costcodes, costcode)) return;

          const projectId = project?.id ?? 'None';
          const startMoment = moment(task.startTime);
          const endMoment = moment(task.endTime);

          if (!(projectId in projectTimeMaps)) {
            projectTimeMaps[projectId] = {
              startTime: startMoment,
              endTime: endMoment,
            };
          }

          if (!(projectId in projectTimeMaps)
              || projectTimeMaps[projectId].startTime.isBefore(startMoment)) {
            projectTimeMaps[projectId].startTime = startMoment;
          }
          if (!(projectId in projectTimeMaps)
              || projectTimeMaps[projectId].endTime.isBefore(endMoment)) {
            projectTimeMaps[projectId].endTime = endMoment;
          }
          const runTime = task.endTime - task.startTime;
          const totalHours = msToHours(runTime);
          projectActualHoursMap[projectId] = (projectActualHoursMap[projectId] || 0) + totalHours;
        });
      });
    }

    let minProjectStart = moment().year(10000);
    let maxProjectEnd = moment().year(0);

    projects.forEach((project) => {
      const {
        name,
        number,
      } = project;

      if (failsProjectFilter(selectedProjects, project, settings)) return;
      const { startDate } = project;
      const { endDate } = project;

      if (!estimatedHours && !(project.id in projectTimeMaps)) return;
      if (!actualHours && (!startDate || !endDate)) return;

      if (!startDate || !endDate) {
        if (!(project.id in projectTimeMaps)) return; // No one has clocked into this project yet
      }
      const startMoment = moment(startDate);
      const endMoment = moment(endDate);

      const projectTaskTimes = projectTimeMaps[project.id] || {};
      const {
        startTime: firstTask,
        endTime: lastTask,
      } = projectTaskTimes;

      if (estimatedHours) {
        minProjectStart = compareIfValid(minProjectStart, startMoment, moment.min);
        maxProjectEnd = compareIfValid(maxProjectEnd, endMoment, moment.max);
      }
      if (actualHours) {
        minProjectStart = compareIfValid(minProjectStart, firstTask, moment.min);
        maxProjectEnd = compareIfValid(maxProjectEnd, lastTask, moment.max);
      }

      const data = {
        ...project,
        key: project.id,
      };

      const projectLabel = project
        ? formatProjectLabelFromCompanySettings({ name, number, settings })
        : null;

      data.name = projectLabel;

      if (estimatedHours) {
        data.startDate = project.startDate ? moment(project.startDate) : null;
        data.endDate = project.endDate ? moment(project.endDate) : null;
      }

      if (actualHours) {
        data.lastTask = lastTask;
        data.firstTask = firstTask;
        data.actualHours = projectActualHoursMap[project.id];
      }
      const minDate = compareMoments(data.startDate, data.firstTask, moment.min);
      const maxDate = compareMoments(data.endDate, data.lastTask, moment.max);
      const totalDuration = Math.ceil(maxDate.diff(minDate, 'month', true)) + 1;
      const percentComplete = project.labourHours
        ? ((data.actualHours || 0) / project.labourHours) * 100
        : null;

      data.maxDate = maxDate;
      data.minDate = minDate;
      data.totalDuration = totalDuration;
      data.percentComplete = percentComplete;

      relevantProj.push(data);
    });

    return {
      minProjectStart,
      maxProjectEnd,
      projects: relevantProj,
    };
  }

  render() {
    const {
      renderColFilters,
    } = this.props;
    const {
      height,
      enabledFilters,
    } = this.state;
    const { minProjectStart, maxProjectEnd, projects } = this.formatData();
    return (
      <GanttChart
        height={height}
        colFilters={renderColFilters(this.getColFilters())}
        minProjectStart={minProjectStart}
        maxProjectEnd={maxProjectEnd}
        projects={projects}
        enabledFilters={enabledFilters}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  costcodeIdMap: state.costcodes.costcodeIdMap,
});

export default withTranslation()(connect(mapStateToProps, {
  searchCostcodeOptions,
  getCostcodeByIds,
})(ProjectTimeline));
